<script lang="ts" setup>
import { useAdmissionReleaseInfo } from '@sancare/typescript-commons/src/composables/useAdmissionReleaseInfo'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { CountryEnum } from '@/store/modules/settings/types'
import { RumFr } from '@/store/modules/stay/mco/types'

const { t } = useI18n()

const props = defineProps<{
  rum: RumFr
  rumIndex: number
}>()

const {
  admissionMode,
  releaseMode,
  origin,
  destination,
  admissionTooltip,
  releaseTooltip,
} = useAdmissionReleaseInfo(() => ({
  admissionMode: props.rum.admissionMode,
  releaseMode: props.rum.releaseMode,
  origin: props.rum.origin,
  destination: props.rum.destination,
}), CountryEnum.FR, t)

const medicalUnitTooltip = computed(() => {
  const um = props.rum.medicalUnit
  if (!um) return null
  const lines: string[] = []
  if (um.title) lines.push(um.title)
  const division = um.facilityDivision
  if (division?.title || division?.facility?.name) {
    lines.push([division?.title, division?.facility?.name].filter(Boolean).join(' — '))
  }
  return lines.join('<br />') || null
})
</script>

<template>
  <div class="d-flex flex-column gap-2">
    <span class="ms-0 text-muted small">
      {{ t('stayDisplayer.rum') }} {{ rumIndex + 1 }}{{ rum.standardModeMajorRum ? '*' : '' }}
    </span>
    <span class="ms-0 fw-bold text-nowrap">
      {{ dayjs(rum.rumStart).format('DD MMM YYYY') }} - {{ dayjs(rum.rumEnd).format('DD MMM YYYY') }}
      ({{ rum.rumEnd.diff(rum.rumStart, 'day') }}{{ t('stayDisplayer.dayShort') }})
    </span>
    <span class="ms-0">
      <span
        v-tooltip="medicalUnitTooltip ? { content: medicalUnitTooltip, placement: 'top', html: true } : null"
        :class="{ 'cursor-pointer': medicalUnitTooltip }"
      >
        {{ t('stayDisplayer.medicalUnitShort') }} {{ rum.medicalUnit?.reference || '-' }}
      </span>
      <span v-if="rum.supervisingDoctorName"> — {{ t('stayDisplayer.doctorPrefix') }} {{ rum.supervisingDoctorName }}</span>
    </span>
    <span class="ms-0">
      <span
        v-tooltip="{
          content: admissionTooltip,
          placement: 'top',
          html: true,
        }"
        class="cursor-pointer"
      >
        {{ t('stayDisplayer.generalStayData.entryDate') }}:
        {{ admissionMode }}/{{ origin }}
      </span>
      —
      <span
        v-tooltip="{
          content: releaseTooltip,
          placement: 'top',
          html: true,
        }"
        class="cursor-pointer"
      >
        {{ t('stayDisplayer.generalStayData.exitDate') }}:
        {{ releaseMode }}/{{ destination }}
      </span>
    </span>
  </div>
</template>
